// NewsWatchlistManager.jsx - Version corrigée sans erreurs de syntaxe
import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Plus, 
  Edit3, 
  Trash2, 
  Mail, 
  Calendar, 
  Tag,
  Settings,
  Check,
  X,
  Loader2,
  AlertCircle,
  CheckCircle
} from 'lucide-react'; 
import { collection, addDoc, getDocs, deleteDoc, updateDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { sendEmail } from '../components/EmailService';

const NewsWatchlistManager = ({ userId, userEmail }) => {
  // États principaux
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // États pour le formulaire
  const [showForm, setShowForm] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [formData, setFormData] = useState({
    theme: 'entrepreneurship',
    frequency: 'weekly',
    keywords: '',
    maxNewsPerEmail: 10,
    qualityMinimum: 6
  });
  
  // États pour la suppression
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);

  // Configuration des thèmes
  const themes = [
    { id: 'entrepreneurship', label: 'Entrepreneuriat', icon: '💼', color: 'bg-blue-500' },
    { id: 'technology', label: 'Technologie', icon: '💻', color: 'bg-green-500' },
    { id: 'finance', label: 'Finance', icon: '💰', color: 'bg-yellow-500' },
    { id: 'leadership', label: 'Leadership', icon: '👥', color: 'bg-purple-500' },
    { id: 'health', label: 'Santé', icon: '🏥', color: 'bg-red-500' },
    { id: 'creativity', label: 'Créativité', icon: '🎨', color: 'bg-pink-500' }
  ];

  const frequencies = [
    { value: 'daily', label: 'Quotidienne', icon: '📅' },
    { value: 'weekly', label: 'Hebdomadaire', icon: '📆' },
    { value: 'bi-weekly', label: 'Bi-hebdomadaire', icon: '🗓️' }
  ];

  useEffect(() => {
    if (userId) {
      loadSubscriptions();
    }
  }, [userId]);

  // Chargement des données
  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const q = query(
        collection(db, 'news_watchlist'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const userSubscriptions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSubscriptions(userSubscriptions);
      console.log(`✅ ${userSubscriptions.length} abonnements chargés`);
      
    } catch (error) {
      console.error('❌ Erreur chargement abonnements:', error);
      setError('Erreur lors du chargement des abonnements');
    } finally {
      setLoading(false);
    }
  };

  // Gestion du formulaire
  const handleCreateSubscription = () => {
    setEditingSubscription(null);
    setFormData({
      theme: 'entrepreneurship',
      frequency: 'weekly',
      keywords: '',
      maxNewsPerEmail: 10,
      qualityMinimum: 6
    });
    setShowForm(true);
  };

  const handleEditSubscription = (subscription) => {
    setEditingSubscription(subscription);
    setFormData({
      theme: subscription.theme,
      frequency: subscription.frequency,
      keywords: subscription.keywords ? subscription.keywords.join(', ') : '',
      maxNewsPerEmail: subscription.maxNewsPerEmail,
      qualityMinimum: subscription.qualityMinimum
    });
    setShowForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!userEmail) {
      setError('Email utilisateur requis pour créer un abonnement');
      return;
    }

    try {
      setError('');
      
      const keywords = formData.keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);

      const subscriptionData = {
        userId,
        userEmail,
        theme: formData.theme,
        frequency: formData.frequency,
        keywords,
        maxNewsPerEmail: parseInt(formData.maxNewsPerEmail),
        qualityMinimum: parseInt(formData.qualityMinimum),
        isActive: true,
        createdAt: new Date().toISOString(),
        lastSent: null,
        totalEmailsSent: 0
      };

      if (editingSubscription) {
        // Mise à jour
        await updateDoc(doc(db, 'news_watchlist', editingSubscription.id), {
          ...subscriptionData,
          updatedAt: new Date().toISOString()
        });
        setSuccessMessage('✅ Abonnement mis à jour avec succès');
      } else {
        // Création
        await addDoc(collection(db, 'news_watchlist'), subscriptionData);
        setSuccessMessage('✅ Abonnement créé avec succès');
      }

      // Recharger les abonnements
      await loadSubscriptions();
      
      // Fermer le formulaire
      setShowForm(false);
      setEditingSubscription(null);

    } catch (error) {
      console.error('❌ Erreur sauvegarde abonnement:', error);
      setError(`Erreur: ${error.message}`);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingSubscription(null);
    setError('');
  };

  // Gestion des abonnements
  const handleToggleSubscription = async (subscriptionId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'news_watchlist', subscriptionId), {
        isActive: !currentStatus,
        updatedAt: new Date().toISOString()
      });
      
      setSuccessMessage(`✅ Abonnement ${!currentStatus ? 'activé' : 'désactivé'}`);
      await loadSubscriptions();
    } catch (error) {
      console.error('❌ Erreur toggle abonnement:', error);
      setError(`Erreur: ${error.message}`);
    }
  };

  const handleDeleteSubscription = (subscription) => {
    setSubscriptionToDelete(subscription);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!subscriptionToDelete) return;

    try {
      await deleteDoc(doc(db, 'news_watchlist', subscriptionToDelete.id));
      setSuccessMessage('✅ Abonnement supprimé');
      await loadSubscriptions();
    } catch (error) {
      console.error('❌ Erreur suppression:', error);
      setError(`Erreur: ${error.message}`);
    } finally {
      setShowDeleteConfirm(false);
      setSubscriptionToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setSubscriptionToDelete(null);
  };

  // Test d'envoi de newsletter
  const handleTestNewsletter = async (subscription) => {
    try {
      setSuccessMessage('📧 Envoi de la newsletter de test...');
      
      const subject = `📰 Test - Votre veille ${getThemeInfo(subscription.theme).label}`;
      const htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; text-align: center;">
            <h1>🗞️ Newsletter de Test</h1>
            <p>Veille ${getThemeInfo(subscription.theme).label}</p>
          </div>
          <div style="padding: 20px;">
            <p>Bonjour,</p>
            <p>Ceci est un test de votre abonnement de veille pour le thème <strong>${getThemeInfo(subscription.theme).label}</strong>.</p>
            <p><strong>Configuration :</strong></p>
            <ul>
              <li>Fréquence : ${getFrequencyInfo(subscription.frequency).label}</li>
              <li>Nombre d'actualités : ${subscription.maxNewsPerEmail}</li>
              <li>Qualité minimum : ${subscription.qualityMinimum}/10</li>
              ${subscription.keywords && subscription.keywords.length > 0 ? `<li>Mots-clés : ${subscription.keywords.join(', ')}</li>` : ''}
            </ul>
            <p>Si vous recevez cet email, votre abonnement fonctionne correctement !</p>
          </div>
          <div style="background: #f8f9fa; padding: 20px; text-align: center; font-size: 12px; color: #666;">
            <p>Test du service de veille Aladinn</p>
          </div>
        </div>
      `;

      await sendEmail(subscription.userEmail, subject, htmlContent);
      setSuccessMessage('✅ Newsletter de test envoyée avec succès !');
      
    } catch (error) {
      console.error('❌ Erreur envoi test:', error);
      setError(`Erreur envoi test: ${error.message}`);
    }
  };

  // Utilitaires
  const getThemeInfo = (themeId) => {
    return themes.find(t => t.id === themeId) || themes[0];
  };

  const getFrequencyInfo = (frequencyValue) => {
    return frequencies.find(f => f.value === frequencyValue) || frequencies[1];
  };

  const formatLastSent = (lastSent) => {
    if (!lastSent) return 'Jamais envoyé';
    
    const date = new Date(lastSent);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Aujourd\'hui';
    if (diffDays === 1) return 'Hier';
    return `Il y a ${diffDays} jours`;
  };

  // Effacer les messages automatiquement
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Rendu du composant
  if (!userId || !userEmail) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-yellow-500" />
          <p className="text-yellow-700">
            Vous devez être connecté pour gérer vos abonnements de veille.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <p className="mt-4 text-gray-600">Chargement de vos abonnements...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* En-tête - VERSION CORRIGÉE */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Bell className="h-8 w-8 text-blue-600" />
            Service de Veille
          </h1>
          <p className="text-gray-600 mt-2">
            Recevez une newsletter personnalisée avec les dernières actualités de vos thèmes préférés
          </p>
        </div>
        {/* Afficher le bouton avec un texte différent s'il y a déjà des abonnements */}
        {subscriptions.length > 0 && (
          <button
            onClick={handleCreateSubscription}
            className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Ajouter un thème
          </button>
        )}
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <p className="text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Informations utilisateur */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
        <div className="flex items-center gap-2 text-blue-800">
          <Mail className="h-5 w-5" />
          <span className="font-medium">Newsletter envoyée à :</span>
          <span>{userEmail}</span>
        </div>
      </div>

      {/* Liste des abonnements */}
      <div className="space-y-4">
        {subscriptions.length > 0 ? (
          subscriptions.map((subscription) => {
            const theme = getThemeInfo(subscription.theme);
            const frequency = getFrequencyInfo(subscription.frequency);
            
            return (
              <div
                key={subscription.id}
                className={`bg-white rounded-lg border-2 p-6 transition-all ${
                  subscription.isActive ? 'border-gray-200' : 'border-gray-100 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`px-3 py-1 rounded-full text-white text-sm font-medium ${theme.color}`}>
                        {theme.icon} {theme.label}
                      </span>
                      <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
                        {frequency.icon} {frequency.label}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        subscription.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {subscription.isActive ? '✓ Actif' : '✗ Inactif'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Actualités par email :</span>
                        <span className="ml-1">{subscription.maxNewsPerEmail}</span>
                      </div>
                      <div>
                        <span className="font-medium">Qualité minimum :</span>
                        <span className="ml-1">{subscription.qualityMinimum}/10</span>
                      </div>
                      <div>
                        <span className="font-medium">Dernière newsletter :</span>
                        <span className="ml-1">{formatLastSent(subscription.lastSent)}</span>
                      </div>
                    </div>

                    {subscription.keywords && subscription.keywords.length > 0 && (
                      <div className="mt-3">
                        <span className="text-sm font-medium text-gray-700">Mots-clés :</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {subscription.keywords.map((keyword, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-3 text-xs text-gray-500">
                      <span>Créé le {new Date(subscription.createdAt).toLocaleDateString('fr-FR')}</span>
                      {subscription.totalEmailsSent > 0 && (
                        <span className="ml-3">• {subscription.totalEmailsSent} emails envoyés</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleTestNewsletter(subscription)}
                      className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      title="Envoyer un test"
                    >
                      <Mail className="h-5 w-5" />
                    </button>
                    
                    <button
                      onClick={() => handleToggleSubscription(subscription.id, subscription.isActive)}
                      className={`p-2 rounded-lg transition-colors ${
                        subscription.isActive
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-400 hover:bg-gray-50'
                      }`}
                      title={subscription.isActive ? 'Désactiver' : 'Activer'}
                    >
                      {subscription.isActive ? <Check className="h-5 w-5" /> : <X className="h-5 w-5" />}
                    </button>
                    
                    <button
                      onClick={() => handleEditSubscription(subscription)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Modifier"
                    >
                      <Edit3 className="h-5 w-5" />
                    </button>
                    
                    <button
                      onClick={() => handleDeleteSubscription(subscription)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📬</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucun abonnement de veille
            </h3>
            <p className="text-gray-600 mb-6">
              Créez votre premier abonnement pour recevoir des newsletters personnalisées.
            </p>
            <button
              onClick={handleCreateSubscription}
              className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium"
            >
              <Plus className="h-5 w-5 inline mr-2" />
              Créer mon premier abonnement
            </button>
          </div>
        )}
      </div>

      {/* Modal de formulaire - VERSION CORRIGÉE */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <h2 className="text-xl font-semibold">
                {editingSubscription ? 'Modifier l\'abonnement' : 'Nouvel abonnement de veille'}
              </h2>
              <button
                onClick={handleCancelForm}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(95vh-140px)]">
              <form onSubmit={handleFormSubmit} className="p-6 space-y-6">
                {/* Sélection du thème */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Thème de veille
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {themes.map((theme) => (
                      <label
                        key={theme.id}
                        className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.theme === theme.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="theme"
                          value={theme.id}
                          checked={formData.theme === theme.id}
                          onChange={(e) => handleFormChange('theme', e.target.value)}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <div className="text-xl mb-1">{theme.icon}</div>
                          <div className="text-sm font-medium">{theme.label}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Fréquence */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Fréquence d'envoi
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {frequencies.map((freq) => (
                      <label
                        key={freq.value}
                        className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.frequency === freq.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="frequency"
                          value={freq.value}
                          checked={formData.frequency === freq.value}
                          onChange={(e) => handleFormChange('frequency', e.target.value)}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <div className="text-lg mb-1">{freq.icon}</div>
                          <div className="text-sm font-medium">{freq.label}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Paramètres avancés */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre d'actualités par email
                    </label>
                    <select
                      value={formData.maxNewsPerEmail}
                      onChange={(e) => handleFormChange('maxNewsPerEmail', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={5}>5 actualités</option>
                      <option value={10}>10 actualités</option>
                      <option value={15}>15 actualités</option>
                      <option value={20}>20 actualités</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Qualité minimum (sur 10)
                    </label>
                    <select
                      value={formData.qualityMinimum}
                      onChange={(e) => handleFormChange('qualityMinimum', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={4}>4/10 - Basique</option>
                      <option value={6}>6/10 - Correct</option>
                      <option value={7}>7/10 - Bon</option>
                      <option value={8}>8/10 - Très bon</option>
                      <option value={9}>9/10 - Excellent</option>
                    </select>
                  </div>
                </div>

                {/* Mots-clés */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mots-clés spécifiques (optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.keywords}
                    onChange={(e) => handleFormChange('keywords', e.target.value)}
                    placeholder="startup, innovation, financement (séparés par des virgules)"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Les actualités seront filtrées pour inclure ces mots-clés
                  </p>
                </div>
              </form>
            </div>

            {/* Boutons fixes en bas */}
            <div className="border-t bg-gray-50 px-6 py-4">
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancelForm}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  onClick={handleFormSubmit}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingSubscription ? 'Mettre à jour' : 'Créer l\'abonnement'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && subscriptionToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Confirmer la suppression
              </h3>
              <p className="text-gray-600 mb-6">
                Êtes-vous sûr de vouloir supprimer l'abonnement de veille pour{' '}
                <strong>{getThemeInfo(subscriptionToDelete.theme).label}</strong> ?
                Cette action est irréversible.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsWatchlistManager;